import { Head, router, usePage } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import type { PageProps } from "@/types/inertia"

type Failure = {
  id: number
  job_class: string | null
  queue: string | null
  failed_at: string
  error_class: string | null
  error_message: string | null
  mail: {
    label: string
    mailer: string
    fields: { label: string; value: string }[]
  } | null
}

type Props = PageProps & {
  failures: Failure[]
  total: number
  limit: number
}

const DESCRIPTION =
  "Work the site tried to do and gave up on. An email that never arrived is here, " +
  "with what it was carrying — send it again once the cause is fixed."

function when(iso: string) {
  return new Date(iso).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  })
}

function FailureCard({ failure }: { failure: Failure }) {
  return (
    <div className="rounded-lg border border-hairline bg-surface p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium text-ink-display">
            {failure.mail?.label || failure.job_class || "Unknown job"}
          </p>
          <p className="mt-1 text-sm text-ink-muted">
            {failure.mail?.mailer ?? failure.job_class}
            {" · failed "}
            {when(failure.failed_at)}
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button
            size="sm"
            onClick={() => router.post(`/admin/jobs/${failure.id}/retry`)}
          >
            Send again
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              if (confirm("Discard this failure? What it was carrying goes with it.")) {
                router.delete(`/admin/jobs/${failure.id}`)
              }
            }}
          >
            Discard
          </Button>
        </div>
      </div>

      {failure.error_class && (
        <p className="mt-3 text-sm text-ink-body">
          <Badge tone="signal">{failure.error_class}</Badge>{" "}
          <span className="text-ink-muted">{failure.error_message}</span>
        </p>
      )}

      {failure.mail && failure.mail.fields.length > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-sm text-ink-muted">
            What it was carrying
          </summary>
          <dl className="mt-2 grid gap-2 sm:grid-cols-[10rem_1fr]">
            {failure.mail.fields.map((field) => (
              <div key={field.label} className="contents">
                <dt className="text-sm text-ink-muted">{field.label}</dt>
                <dd className="text-sm whitespace-pre-wrap text-ink-body">{field.value}</dd>
              </div>
            ))}
          </dl>
        </details>
      )}
    </div>
  )
}

export default function JobsIndex() {
  const { props } = usePage<Props>()
  const { failures, total, limit } = props

  return (
    <AdminShell>
      <Head title="Failed jobs">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Failed jobs" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>

      <PageHeader title="Failed jobs" description={DESCRIPTION} />

      {props.flash?.notice && <p className="mt-4 text-sm text-accent">{props.flash.notice}</p>}

      {failures.length === 0 ? (
        <p className="mt-6 text-ink-muted">
          Nothing has failed. Emails are being delivered.
        </p>
      ) : (
        <>
          {total > limit && (
            <p className="mt-4 text-sm text-ink-muted">
              Showing the most recent {limit} of {total}.
            </p>
          )}
          <div className="mt-6 grid gap-3">
            {failures.map((failure) => (
              <FailureCard key={failure.id} failure={failure} />
            ))}
          </div>
        </>
      )}
    </AdminShell>
  )
}
